import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Wallet, TrendingUp, CalendarCheck, CalendarClock } from "lucide-react";

export const CAT_LABELS = {
  flights: "Flights", lodging: "Lodging", car_rental: "Car rental", activities: "Activities",
  food: "Food & dining", transport: "Local transport", shopping: "Shopping", misc: "Misc",
  unallocated: "Unallocated (actual)",
};

export function fmtUSD(v, digits = 0) {
  if (v === null || v === undefined) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: digits }).format(v);
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function SummaryCards({ cards }) {
  const items = [
    { key: "spent_past", label: "Spent · past trips", value: cards.spent_past, icon: Wallet, color: "text-sand-900" },
    { key: "upcoming_confirmed", label: "Upcoming · confirmed", value: cards.upcoming_confirmed, icon: CalendarCheck, color: "text-ember" },
    { key: "upcoming_planned", label: "Upcoming · planned", value: cards.upcoming_planned, icon: CalendarClock, color: "text-amber-600" },
    { key: "total", label: "Total", value: cards.total, icon: TrendingUp, color: "text-sand-900" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="budget-cards">
      {items.map(({ key, label, value, icon: Icon, color }) => (
        <div key={key} className="bg-surface border-2 border-ink p-5" data-testid={`budget-card-${key}`}>
          <div className="flex items-center gap-2 mb-2">
            <Icon size={13} className="text-sand-400" />
            <p className="label-tag">{label}</p>
          </div>
          <p className={`font-display font-medium text-3xl tracking-tight ${color}`}>{fmtUSD(value)}</p>
        </div>
      ))}
    </div>
  );
}

function MonthBars({ byMonth, year }) {
  let months;
  if (year !== "all") {
    const map = Object.fromEntries(byMonth.map((m) => [m.month, m]));
    months = MONTH_NAMES.map((name, i) => {
      const key = `${year}-${String(i + 1).padStart(2, "0")}`;
      return { label: name, ...(map[key] || { month: key, effective: 0, estimated: 0, trips: 0 }) };
    });
  } else {
    months = byMonth.map((m) => ({ ...m, label: m.month }));
  }
  const max = Math.max(...months.map((m) => m.effective), 1);
  return (
    <div className="bg-surface rounded-card border border-hairline p-6 h-full" data-testid="budget-month-bars">
      <p className="label-tag mb-6">By month · effective spend</p>
      {months.length === 0 ? (
        <p className="text-sm text-sand-500">No dated trips in this range.</p>
      ) : (
        <div className="flex items-end gap-1.5 h-44">
          {months.map((m) => (
            <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full min-w-0 group" data-testid={`month-bar-${m.month}`}>
              {m.effective > 0 && (
                <p className="font-mono text-[9px] text-sand-500 mb-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  {fmtUSD(m.effective)}
                </p>
              )}
              <div className="w-full bg-ember transition-all" style={{ height: `${(m.effective / max) * 100}%`, minHeight: m.effective > 0 ? 3 : 0 }}
                title={`${m.label}: ${fmtUSD(m.effective)} · ${m.trips} trip${m.trips !== 1 ? "s" : ""}`} />
              <p className="font-mono text-[9px] uppercase text-sand-500 mt-1.5 truncate w-full text-center">{m.label}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryBreakdown({ byCategory }) {
  const max = Math.max(...byCategory.map((c) => Math.abs(c.effective)), 1);
  return (
    <div className="bg-surface rounded-card border border-hairline p-6 h-full" data-testid="budget-category-breakdown">
      <p className="label-tag mb-6">By expense type</p>
      {byCategory.length === 0 ? (
        <p className="text-sm text-sand-500">No costs recorded yet — add costs to reservations or expense line items on a trip.</p>
      ) : (
        <div className="space-y-3">
          {byCategory.map((c) => (
            <div key={c.category} data-testid={`category-row-${c.category}`}>
              <div className="flex items-baseline justify-between mb-1">
                <p className="text-xs font-medium">{CAT_LABELS[c.category] || c.category}</p>
                <p className="font-mono text-xs font-semibold">{fmtUSD(c.effective)}</p>
              </div>
              <div className="h-1.5 bg-sand-100">
                <div className={c.category === "unallocated" ? "h-full bg-sand-400" : "h-full bg-ember"}
                  style={{ width: `${Math.min((Math.abs(c.effective) / max) * 100, 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EntityBreakdown({ byEntity }) {
  if (!byEntity?.length) return null;
  const max = Math.max(...byEntity.map((b) => b.effective), 1);
  return (
    <div className="bg-surface rounded-card border border-hairline p-6" data-testid="budget-entity-breakdown">
      <p className="label-tag mb-4">By entity</p>
      <div className="space-y-3">
        {byEntity.map((b) => (
          <div key={b.entity} data-testid={`entity-row-${b.entity}`}>
            <div className="flex items-baseline justify-between mb-1">
              <p className="text-xs font-medium">{b.entity === "unassigned" ? "Unassigned" : b.entity}
                <span className="font-mono text-[10px] text-sand-400 ml-2">{b.trips} trip{b.trips !== 1 ? "s" : ""}</span>
              </p>
              <p className="font-mono text-xs font-semibold">{fmtUSD(b.effective)}</p>
            </div>
            <div className="h-1.5 bg-sand-100">
              <div className={b.entity === "unassigned" ? "h-full bg-sand-400" : "h-full bg-sand-900"}
                style={{ width: `${Math.min((b.effective / max) * 100, 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const STATUS_STYLE = {
  past: "bg-sand-100 text-sand-600",
  confirmed: "bg-ember text-white",
  planned: "bg-amber-100 text-amber-800",
};

const COLUMNS = [
  { h: "Trip" }, { h: "Dates" }, { h: "Status" },
  { h: "Estimated", tip: "Reservation costs (parsed from emails or entered manually) plus expense line items" },
  { h: "Actual", tip: "The real total you recorded for the trip afterwards" },
  { h: "Effective", tip: "Actual where you've entered one, otherwise the estimate — this is the number used in all totals and charts" },
];

function TripTable({ trips }) {
  return (
    <div className="bg-surface rounded-card border border-hairline" data-testid="budget-trip-table">
      <div className="px-6 pt-6 pb-3 flex items-baseline justify-between flex-wrap gap-x-4 gap-y-1">
        <p className="label-tag">By trip</p>
        <p className="text-[11px] text-sand-500" data-testid="budget-effective-explainer">
          Effective = actual where you've entered one, otherwise the estimate.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-ink text-left">
              {COLUMNS.map(({ h, tip }, i) => (
                <th key={h} title={tip} className={`label-tag px-6 py-2 font-normal ${i >= 3 ? "text-right" : ""} ${tip ? "cursor-help underline decoration-dotted decoration-sand-300 underline-offset-4" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trips.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-8 text-center text-sand-500">No trips in this range.</td></tr>
            )}
            {trips.map((t) => {
              const delta = t.actual_total !== null && t.est_total > 0 ? t.actual_total - t.est_total : null;
              return (
                <tr key={t.trip_id} className="border-b border-sand-100 hover:bg-sand-50" data-testid={`budget-trip-row-${t.trip_id}`}>
                  <td className="px-6 py-3">
                    <Link to={`/trips/${t.trip_id}`} className="font-medium hover:text-ember hover:underline" data-testid={`budget-trip-link-${t.trip_id}`}>
                      {t.name}
                    </Link>
                    {t.destination && <p className="text-[11px] text-sand-500">{t.destination}</p>}
                  </td>
                  <td className="px-6 py-3 font-mono text-xs text-sand-600 whitespace-nowrap">
                    {t.start_date ? `${t.start_date}${t.end_date && t.end_date !== t.start_date ? ` → ${t.end_date}` : ""}` : "—"}
                  </td>
                  <td className="px-6 py-3">
                    <span className={`font-mono text-[10px] uppercase tracking-wide px-2 py-0.5 ${STATUS_STYLE[t.status]}`}>{t.status}</span>
                  </td>
                  <td className="px-6 py-3 text-right font-mono">{fmtUSD(t.est_total)}</td>
                  <td className="px-6 py-3 text-right font-mono">
                    {t.actual_total !== null ? fmtUSD(t.actual_total) : <span className="text-sand-400">—</span>}
                    {delta !== null && Math.abs(delta) >= 1 && (
                      <span className={`block text-[10px] ${delta > 0 ? "text-error" : "text-emerald-600"}`}>
                        {delta > 0 ? "+" : ""}{fmtUSD(delta)} vs est
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right font-mono font-semibold" data-testid={`trip-effective-${t.trip_id}`}>{fmtUSD(t.eff_total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Budget() {
  const currentYear = String(new Date().getFullYear());
  const [year, setYear] = useState(currentYear);
  const [entity, setEntity] = useState("all");
  const [data, setData] = useState(null);

  useEffect(() => {
    const q = entity !== "all" ? `&entity=${encodeURIComponent(entity)}` : "";
    api.get(`/budget/summary?year=${year}${q}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load budget"));
  }, [year, entity]);

  const yearTabs = data ? [...new Set([...(data.years || []), currentYear])].sort().reverse() : [currentYear];

  return (
    <div>
      <main className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-10 md:py-16" data-testid="budget-page">
        <div className="mb-10">
          <p className="label-tag mb-3">Budget</p>
          <h1 className="heading-h1 text-4xl sm:text-5xl lg:text-6xl mb-2">Travel spend.</h1>
          <p className="text-sand-600 text-base mt-3">
            Past expenses plus committed and planned trips — actuals override estimates where you've entered them.
          </p>
          <div className="divider-strong mt-4" />
        </div>

        <div className="flex gap-2 mb-8 flex-wrap" data-testid="budget-year-tabs">
          {yearTabs.map((y) => (
            <button key={y} onClick={() => setYear(y)}
              className={`btn-swiss text-sm ${year === y ? "btn-primary" : "btn-outline"}`}
              data-testid={`budget-year-${y}`}>
              {y}
            </button>
          ))}
          <button onClick={() => setYear("all")}
            className={`btn-swiss text-sm ${year === "all" ? "btn-primary" : "btn-outline"}`}
            data-testid="budget-year-all">
            All time
          </button>
        </div>

        {data?.entities?.length > 0 && (
          <div className="flex gap-2 mb-8 flex-wrap items-center" data-testid="budget-entity-tabs">
            <span className="label-tag mr-1">Entity</span>
            <button onClick={() => setEntity("all")}
              className={`font-mono text-[11px] uppercase tracking-wide px-3 py-1.5 border transition-colors ${entity === "all" ? "border-ink bg-black text-white" : "border-sand-300 text-sand-600 hover:border-ink"}`}
              data-testid="budget-entity-all">
              All
            </button>
            {data.entities.map((n) => (
              <button key={n} onClick={() => setEntity(n)}
                className={`font-mono text-[11px] uppercase tracking-wide px-3 py-1.5 border transition-colors ${entity === n ? "border-ink bg-black text-white" : "border-sand-300 text-sand-600 hover:border-ink"}`}
                data-testid={`budget-entity-${n}`}>
                {n}
              </button>
            ))}
            <button onClick={() => setEntity("unassigned")}
              className={`font-mono text-[11px] uppercase tracking-wide px-3 py-1.5 border transition-colors ${entity === "unassigned" ? "border-ink bg-black text-white" : "border-sand-300 text-sand-600 hover:border-ink"}`}
              data-testid="budget-entity-unassigned">
              Unassigned
            </button>
          </div>
        )}

        {data === null ? (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-28 bg-surface rounded-card border border-hairline animate-pulse" />)}</div>
            <div className="h-64 bg-surface rounded-card border border-hairline animate-pulse" />
          </div>
        ) : (
          <div className="space-y-4">
            <SummaryCards cards={data.cards} />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-7"><MonthBars byMonth={data.by_month} year={year} /></div>
              <div className="lg:col-span-5 flex flex-col gap-4">
                <div className="flex-1 min-h-0"><CategoryBreakdown byCategory={data.by_category} /></div>
                {entity === "all" && <EntityBreakdown byEntity={data.by_entity} />}
              </div>
            </div>
            <TripTable trips={data.trips} />
          </div>
        )}
      </main>
    </div>
  );
}
